import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import { useNavigate } from "react-router-dom";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import {
  useAddCommunityTriggerMutation,
  useDeleteCommunityTriggerMutation,
  useGetAllCommunityTriggersQuery,
  useUpdateCommunityTriggerMutation,
} from "apis/communityTriggers";
import CommunityTriggerEdit from "components/communityTriggers/edit";
import CommunityTriggerView from "components/communityTriggers/view";
import Button from "components/ui/Button";
import Table from "components/ui/Table";

const CommunityTriggers = () => {
  const pageSize = 10;
  const navigate = useNavigate();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { mutate: updateTrigger } = useUpdateCommunityTriggerMutation();
  const { triggers = [], isPending: isTriggerLoading } = useGetAllCommunityTriggersQuery();
  const { mutate: addTrigger } = useAddCommunityTriggerMutation();
  const { mutate: deleteTrigger } = useDeleteCommunityTriggerMutation();

  const renderItemsWithQuantity = (items) => {
    if (!Array.isArray(items) || items.length === 0) return "-";

    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item, index) => {
          // Handle both old format (direct strings/objects) and new format (with optionId/quantity)
          let displayName = "";
          let quantity = "";

          if (typeof item === "string") {
            // Old format: direct string
            displayName = item;
          } else if (item?.optionId) {
            // New format: {optionId: ObjectId, quantity: Number}
            displayName = item.optionId?.name || item.optionId;
            quantity = item.quantity;
          } else if (item?.name) {
            // Populated object format
            displayName = item.name;
          } else {
            displayName = item;
          }

          return (
            <span
              key={index}
              className="bg-mediumgray px-2 py-1 rounded text-xs inline-flex items-center gap-1"
            >
              <span>{displayName}</span>
              {quantity && (
                <span className="bg-yellow-100 text-yellow-800 font-semibold px-1 rounded text-sm">
                  {quantity}
                </span>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "trigger",
        header: "Trigger",
        isFilter: true,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => row.type?.name || row.type || "-",
        isFilter: true,
      },
      {
        accessorKey: "vessel",
        header: "Vessel",
        cell: (row) => renderItemsWithQuantity(row.vessel || []),
        isFilter: false,
      },
      {
        accessorKey: "prompt",
        header: "Prompt",
        cell: (row) => {
          const prompt = row.prompt;
          return prompt ? (
            <div className="max-w-xs truncate" title={prompt}>
              {prompt}
            </div>
          ) : (
            "-"
          );
        },
        isFilter: true,
      },
      {
        accessorKey: "receiver",
        header: "Receiver",
        cell: (row) => renderItemsWithQuantity(row.receiver || []),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (isTriggerLoading) {
    return <PageLoader />;
  }

  const handleDelete = () => {
    deleteTrigger(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddTriggerButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={16} className="mr-2" />
      Add Trigger
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Trigger"
        className="max-w-2xl"
      >
        <CommunityTriggerEdit
          initialData={triggers.find((trigger) => trigger._id === selectedId)}
          onSubmit={(formData) => {
            updateTrigger({ id: selectedId, ...formData });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Trigger"
        className="max-w-2xl"
      >
        <CommunityTriggerEdit
          initialData=""
          onSubmit={(formData) => {
            addTrigger(formData);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Trigger"
      >
        <div>
          Are you sure you want to delete this trigger?
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button onClick={handleDelete} className="black-button">
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Trigger Details"
        className="max-w-2xl"
      >
        <CommunityTriggerView
          data={triggers.find((trigger) => trigger._id === selectedId)}
        />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Community Triggers"
          columns={columns}
          data={triggers || []}
          pageSize={pageSize}
          ButtonCmp={AddTriggerButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default CommunityTriggers;
